import { Grid, Switch as SwitchButton } from '@mui/material';
import { HSpacer, RoutalTooltip } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { GetDescriptionFields } from '.';
import { HeaderContent } from '../../../components/HeaderContent';
import * as routing from '../../../constants/Routing';
import { Project } from '../../../domain/project/Project';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useUpdateProject } from '../../../hooks/project/useUpdateProject';
import { useRetrieveSupportDocs } from '../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { ObjectOfAny } from '../../../interfaces/common';
import Text from '../../../react-components/Text';
import { DataTable } from '../../../react-components/data-table/DataTable';
import { getProjectFieldsConstraints } from './ProjectFieldsConstraints';

type ProjectSettingsPlannerFieldsProps = {
  project: Project;
};

const ProjectSettingsPlannerFields = ({ project }: ProjectSettingsPlannerFieldsProps) => {
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { run: editProject } = useUpdateProject({ project });
  const { data: user } = useRetrieveUser();
  const [constraints, setConstraints] = useState({});
  const [descriptionFields, setDescriptionFields] = useState<ObjectOfAny>({
    vehicle: {},
    client: {},
    service_report_completed: {},
    service_report_canceled: {},
  });

  useEffect(() => {
    if (project) {
      if (project.view) {
        setConstraints(project.view.constraints || {});
        setDescriptionFields({
          vehicle: project.view.vehicle || {},
          client: project.view.client || {},
        });
      }
    }
  }, [project]);

  if (!project) return null;

  return (
    <div style={{ padding: `16px 22px` }}>
      <Routes>
        <Route
          index
          element={
            <Navigate to={routing.PROJECT_SETTINGS.PLANNER_FIELDS.CONSTRAINTS.replace(`:projectId`, project.id)} />
          }
        />

        <Route
          path="/constraints"
          element={
            <>
              <Grid container direction="column" style={{ padding: `16px 22px` }}>
                <Grid container direction="row" wrap="nowrap">
                  <HeaderContent
                    titleIntlId="projects.settings.fields.constraints.title"
                    article={{
                      intlId: `projects.settings.fields.constraints.article`,
                      link: retrieveDoc({ supportPage: `settingsFields`, language: user?.language.value }),
                    }}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                direction="column"
                style={{
                  padding: `16px 22px`,
                }}
              >
                <DataTable
                  noFooter
                  docs={Object.keys(getProjectFieldsConstraints(user?.isAdmin() || user?.isRoot())).map((key) => ({
                    doc: { id: key },
                  }))}
                  total={Object.keys(getProjectFieldsConstraints(user?.isAdmin() || user?.isRoot())).length}
                  showCheckboxes={false}
                  columns={[
                    {
                      width: `calc(100% - 135px)`,
                      accessorName: `name`,
                      name: <Text variant="body" intlId="projects.settings.fields.column.name" />,
                      formatter: ({ doc }) => {
                        const { id: setting } = doc;
                        const constraint = (getProjectFieldsConstraints(user?.isAdmin() || user?.isRoot()) as any)[
                          setting
                        ];
                        return (
                          <Grid container direction="column" wrap="nowrap">
                            <Grid item container direction="row">
                              <Grid item container style={{ width: `unset` }}>
                                <img
                                  alt=""
                                  src={constraint.iconSrc}
                                  style={{ width: `12px`, height: `12px` }}
                                  draggable="false"
                                />
                              </Grid>

                              <HSpacer small />

                              <Grid item>
                                {constraint.description ? (
                                  <RoutalTooltip
                                    id="field-description"
                                    placement="top"
                                    overlayStyle={{
                                      maxWidth: `300px`,
                                    }}
                                    title={intl.formatMessage({
                                      id: constraint.description,
                                    })}
                                  >
                                    <Text
                                      variant="body-large"
                                      intlId={`projects.settings.fields.constraints.${setting}`}
                                    />
                                  </RoutalTooltip>
                                ) : (
                                  <Text
                                    variant="body-large"
                                    intlId={`projects.settings.fields.constraints.${setting}`}
                                  />
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        );
                      },
                    },
                    {
                      accessorName: `enabled`,
                      align: `right`,
                      name: <Text variant="body" intlId="enabled" />,
                      formatter: ({ doc }) => {
                        const { id: setting } = doc;
                        return (
                          <Grid container>
                            <Grid item>
                              <SwitchButton
                                checked={(constraints as Record<string, boolean>)[setting] || false}
                                onChange={(event: any) => {
                                  const { checked } = event.target;
                                  (project.view.constraints as any)[setting] = checked;
                                  editProject({ view: project.view });
                                }}
                                color="primary"
                              />
                            </Grid>
                          </Grid>
                        );
                      },
                    },
                  ]}
                />
              </Grid>
            </>
          }
        />

        <Route
          path="/vehicles"
          element={
            <>
              <Grid container direction="column" style={{ padding: `16px 22px` }}>
                <Grid container direction="row" wrap="nowrap">
                  <HeaderContent
                    titleIntlId="projects.settings.fields.vehicle.title"
                    subtitle="projects.settings.fields.vehicle.subtitle"
                  />
                </Grid>
              </Grid>
              <GetDescriptionFields
                showCustomFields
                type="vehicle"
                project={project}
                descriptionFields={descriptionFields}
              />
            </>
          }
        />

        <Route
          path="/clients"
          element={
            <>
              <Grid container direction="column" style={{ padding: `16px 22px` }}>
                <Grid container direction="row" wrap="nowrap">
                  <HeaderContent
                    titleIntlId="projects.settings.fields.client.title"
                    subtitle="projects.settings.fields.client.subtitle"
                  />
                </Grid>
              </Grid>
              <GetDescriptionFields
                showCustomFields
                type="client"
                project={project}
                descriptionFields={descriptionFields}
              />
            </>
          }
        />

        <Route
          path="*"
          element={
            <Navigate to={routing.PROJECT_SETTINGS.PLANNER_FIELDS.CONSTRAINTS.replace(`:projectId`, project.id)} />
          }
        />
      </Routes>
    </div>
  );
};

export default ProjectSettingsPlannerFields;
